/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * This file is part of Mythbound Adventure Studio, a proprietary software project.
 * Unauthorized copying, modification, distribution, or use of this software,
 * via any medium, is strictly prohibited without explicit written permission
 * from the copyright holders.
 *
 * For licensing inquiries, contact: taleweavertik@gmail.com
 *
 * @fileoverview Voice playback service with jitter buffer for web audio
 * @version 2.0.0
 * @since v0.0.07
 *
 * Purpose: Handle inbound audio frames from backend (STS/TTS output) and play
 * locally with minimal jitter while honoring buffer settings.
 *
 * Pipeline: WS binary receive → JitterBuffer → AudioContext playback
 *
 * Enhanced: December 16, 2025 (Agent A - Voice Pipeline)
 * - Added adaptive jitter buffering with auto-detection (PRD 3)
 * - Connection quality assessment every 2 seconds
 * - Hysteresis: 5 consecutive readings before preset switch
 * - Manual preset lock with localStorage persistence
 *
 * Dependencies:
 * - websocketService for receiving binary voice data
 * - VOICE_BRIDGE_CONFIG for audio parameters
 */

import { webSocketService } from '../websocket/websocketService';
import { errorManager, ErrorSeverity } from '../error/ErrorManager';
import { VOICE_BRIDGE_CONFIG } from '../../config/audioSettings';

/**
 * Buffer preset profiles for different network conditions
 * Low-latency: responsive but may have glitches on unstable connections
 * Smooth: higher latency but more resilient to network jitter
 */
export type BufferPreset = 'responsive' | 'balanced' | 'smooth';

export interface BufferPresetConfig {
  name: BufferPreset;
  bufferMs: number;
  minBufferMs: number;
  maxBufferMs: number;
  description: string;
}

export const BUFFER_PRESETS: Record<BufferPreset, BufferPresetConfig> = {
  responsive: {
    name: 'responsive',
    bufferMs: 50,
    minBufferMs: 25,
    maxBufferMs: 100,
    description: 'Low latency (50ms) - Best for fast connections'
  },
  balanced: {
    name: 'balanced',
    bufferMs: 100,
    minBufferMs: 50,
    maxBufferMs: 200,
    description: 'Balanced (100ms) - Good for most connections'
  },
  smooth: {
    name: 'smooth',
    bufferMs: 150,
    minBufferMs: 100,
    maxBufferMs: 300,
    description: 'Smooth (150ms) - Best for unstable connections'
  }
};

/**
 * Audio packet queued in the jitter buffer
 */
interface BufferedPacket {
  pcmData: Int16Array;
  timestamp: number;
  sequence: number;
  participantId: string;
  receivedAt: number;
}

/**
 * Playback statistics for diagnostics
 */
export interface PlaybackStats {
  packetsReceived: number;
  packetsPlayed: number;
  packetsDropped: number;
  bufferUnderruns: number;
  bufferOverruns: number;
  currentBufferMs: number;
  averageLatencyMs: number;
  jitterMs: number;
}

/**
 * Voice Playback Service
 *
 * Manages audio playback from backend voice streams with jitter buffering.
 * Subscribes to WebSocket voice_data events and plays audio through Web Audio API.
 *
 * Features:
 * - Configurable jitter buffer with presets (responsive/balanced/smooth)
 * - Automatic buffer management to minimize glitches
 * - Per-participant audio mixing
 * - Playback statistics for diagnostics
 */
export class VoicePlaybackService {
  private audioContext: AudioContext | null = null;
  private gainNode: GainNode | null = null;

  // Jitter buffer configuration
  private bufferPreset: BufferPreset = 'balanced';
  private targetBufferMs: number = BUFFER_PRESETS.balanced.bufferMs;

  // Packet buffer (sorted by sequence)
  private packetBuffer: BufferedPacket[] = [];
  private nextExpectedSequence: number = 0;
  private lastPlayedTime: number = 0;

  // Playback scheduling
  private scheduledEndTime: number = 0;
  private isPlaying: boolean = false;
  private playbackInterval: ReturnType<typeof setInterval> | null = null;

  // Statistics
  private stats: PlaybackStats = {
    packetsReceived: 0,
    packetsPlayed: 0,
    packetsDropped: 0,
    bufferUnderruns: 0,
    bufferOverruns: 0,
    currentBufferMs: 0,
    averageLatencyMs: 0,
    jitterMs: 0
  };
  private latencySum: number = 0;
  private latencyCount: number = 0;
  private lastLatency: number = 0;
  private jitterSum: number = 0;

  // Adaptive buffering (PRD 3)
  private static readonly JITTER_WINDOW_SIZE = 20;
  private static readonly QUALITY_CHECK_INTERVAL_MS = 2000;
  private static readonly HYSTERESIS_THRESHOLD = 5;
  private static readonly JITTER_THRESHOLD_RESPONSIVE = 20; // ms
  private static readonly JITTER_THRESHOLD_BALANCED = 50; // ms
  private static readonly LOCAL_STORAGE_PRESET_KEY = 'voiceBufferPreset';
  private static readonly LOCAL_STORAGE_LOCK_KEY = 'voiceBufferPresetLocked';
  
  private jitterSamples: number[] = [];
  private qualityCheckInterval: ReturnType<typeof setInterval> | null = null;
  private consecutiveQualityReadings: number = 0;
  private lastQualityPreset: BufferPreset = 'balanced';
  private presetLocked: boolean = false;
  private lastPacketArrivalTime: number = 0;

  // Event callbacks
  private onPlaybackStartCallback: (() => void) | null = null;
  private onPlaybackStopCallback: (() => void) | null = null;
  private onBufferStatusCallback: ((bufferMs: number, preset: BufferPreset) => void) | null = null;
  private onErrorCallback: ((error: Error, context: string) => void) | null = null;

  // Bound handler reference for proper subscribe/unsubscribe matching
  // Must be stored as instance property to ensure the same reference is used
  // for both subscribe() and unsubscribe() calls
  private boundHandleVoiceData: (data: {
    rawPCM: Uint8Array;
    metadata: {
      sessionId: string;
      participantId: string;
      audioFormat: string;
      sampleRate: number;
      channels: number;
      bitDepth: number;
      frameSize: number;
      durationMs: number | null;
    };
  }) => void;

  constructor() {
    // Bind handler once in constructor to ensure same reference for subscribe/unsubscribe
    this.boundHandleVoiceData = this.handleVoiceData.bind(this);
  }

  /**
   * Initialize the playback service
   * Sets up AudioContext and subscribes to voice events
   *
   * @returns Promise<boolean> - True if initialization succeeded
   */
  async initialize(): Promise<boolean> {
    try {
      // Create AudioContext at correct sample rate
      this.audioContext = new AudioContext({
        sampleRate: VOICE_BRIDGE_CONFIG.SAMPLE_RATE,
        latencyHint: 'interactive'
      });

      // Resume if suspended (Chrome autoplay policy)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Create master gain node for volume control
      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.value = 1.0;
      this.gainNode.connect(this.audioContext.destination);

      // Subscribe to voice data events from WebSocket
      // Uses pre-bound handler to ensure same reference for unsubscribe
      webSocketService.subscribe('voice_data', this.boundHandleVoiceData);

      console.log('VoicePlaybackService initialized');
      return true;
    } catch (error) {
      this.onErrorCallback?.(error as Error, 'initialize');
      errorManager.reportError(
        'Failed to initialize VoicePlaybackService.',
        ErrorSeverity.ERROR,
        'VoicePlaybackService.initialize',
        error
      );
      return false;
    }
  }

  /**
   * Set event callbacks for UI integration
   */
  setCallbacks(callbacks: {
    onPlaybackStart?: () => void;
    onPlaybackStop?: () => void;
    onBufferStatus?: (bufferMs: number, preset: BufferPreset) => void;
    onError?: (error: Error, context: string) => void;
  }): void {
    if (callbacks.onPlaybackStart) this.onPlaybackStartCallback = callbacks.onPlaybackStart;
    if (callbacks.onPlaybackStop) this.onPlaybackStopCallback = callbacks.onPlaybackStop;
    if (callbacks.onBufferStatus) this.onBufferStatusCallback = callbacks.onBufferStatus;
    if (callbacks.onError) this.onErrorCallback = callbacks.onError;
  }

  /**
   * Set the buffer preset for jitter buffer sizing
   *
   * @param preset - Buffer preset name
   */
  setBufferPreset(preset: BufferPreset): void {
    this.bufferPreset = preset;
    this.targetBufferMs = BUFFER_PRESETS[preset].bufferMs;
    console.log(`Buffer preset set to ${preset} (${this.targetBufferMs}ms)`);
    this.onBufferStatusCallback?.(this.targetBufferMs, preset);
  }

  /**
   * Get current buffer preset
   */
  getBufferPreset(): BufferPreset {
    return this.bufferPreset;
  }

  /**
   * Check if preset is locked (manual override active)
   */
  isPresetLocked(): boolean {
    return this.presetLocked;
  }

  /**
   * Start automatic quality detection and adaptive buffering
   * Call after initialize()
   * 
   * Implements PRD 3: Auto-detect connection quality via jitter measurement
   * and switch presets automatically with hysteresis.
   */
  startAdaptiveBuffering(): void {
    // Check for locked preset in localStorage
    const savedLock = localStorage.getItem(VoicePlaybackService.LOCAL_STORAGE_LOCK_KEY);
    if (savedLock === 'true') {
      this.presetLocked = true;
      const savedPreset = localStorage.getItem(VoicePlaybackService.LOCAL_STORAGE_PRESET_KEY) as BufferPreset | null;
      if (savedPreset && BUFFER_PRESETS[savedPreset]) {
        this.setBufferPreset(savedPreset);
      }
      console.log('Adaptive buffering disabled - preset locked to', this.bufferPreset);
      return;
    }

    if (this.qualityCheckInterval) {
      // Already running
      return;
    }

    this.qualityCheckInterval = setInterval(() => {
      this.assessConnectionQuality();
    }, VoicePlaybackService.QUALITY_CHECK_INTERVAL_MS);

    console.log('Adaptive buffering started');
  }

  /**
   * Stop automatic quality detection
   */
  stopAdaptiveBuffering(): void {
    if (this.qualityCheckInterval) {
      clearInterval(this.qualityCheckInterval);
      this.qualityCheckInterval = null;
      console.log('Adaptive buffering stopped');
    }
  }

  /**
   * Assess connection quality and adjust buffer preset
   * Called every QUALITY_CHECK_INTERVAL_MS
   */
  private assessConnectionQuality(): void {
    if (this.presetLocked) return;

    // Need minimum samples for reliable assessment
    if (this.jitterSamples.length < 10) return;

    const avgJitter = this.calculateAverageJitter();
    const recommendedPreset = this.getPresetForJitter(avgJitter);

    // Track consecutive readings for hysteresis
    if (recommendedPreset === this.lastQualityPreset) {
      this.consecutiveQualityReadings++;
    } else {
      this.consecutiveQualityReadings = 1;
      this.lastQualityPreset = recommendedPreset;
    }

    // Require HYSTERESIS_THRESHOLD consecutive readings before switching
    if (
      this.consecutiveQualityReadings >= VoicePlaybackService.HYSTERESIS_THRESHOLD &&
      recommendedPreset !== this.bufferPreset
    ) {
      console.log(
        `Adaptive buffer: switching to ${recommendedPreset} ` +
        `(jitter: ${avgJitter.toFixed(1)}ms, consecutive: ${this.consecutiveQualityReadings})`
      );
      this.setBufferPreset(recommendedPreset);
    }
  }

  /**
   * Calculate average jitter from recent samples
   * Uses mean absolute deviation of inter-arrival times
   */
  private calculateAverageJitter(): number {
    if (this.jitterSamples.length === 0) return 0;

    const sum = this.jitterSamples.reduce((a, b) => a + b, 0);
    return sum / this.jitterSamples.length;
  }

  /**
   * Determine recommended preset based on jitter level
   * 
   * Thresholds (from PRD 3):
   * - Jitter < 20ms → responsive (50ms buffer)
   * - Jitter 20-50ms → balanced (100ms buffer)
   * - Jitter > 50ms → smooth (150ms buffer)
   */
  private getPresetForJitter(jitterMs: number): BufferPreset {
    if (jitterMs < VoicePlaybackService.JITTER_THRESHOLD_RESPONSIVE) {
      return 'responsive';
    }
    if (jitterMs < VoicePlaybackService.JITTER_THRESHOLD_BALANCED) {
      return 'balanced';
    }
    return 'smooth';
  }

  /**
   * Lock preset to prevent automatic switching
   * Stores preference in localStorage for persistence
   * 
   * @param preset - Preset to lock to
   */
  lockPreset(preset: BufferPreset): void {
    this.presetLocked = true;
    this.setBufferPreset(preset);
    localStorage.setItem(VoicePlaybackService.LOCAL_STORAGE_PRESET_KEY, preset);
    localStorage.setItem(VoicePlaybackService.LOCAL_STORAGE_LOCK_KEY, 'true');
    console.log(`Buffer preset locked to ${preset}`);

    // Stop adaptive checks while locked
    this.stopAdaptiveBuffering();
  }

  /**
   * Unlock preset to allow automatic switching
   * Removes preference from localStorage
   */
  unlockPreset(): void {
    this.presetLocked = false;
    localStorage.removeItem(VoicePlaybackService.LOCAL_STORAGE_PRESET_KEY);
    localStorage.removeItem(VoicePlaybackService.LOCAL_STORAGE_LOCK_KEY);
    
    // Reset hysteresis tracking
    this.consecutiveQualityReadings = 0;
    this.lastQualityPreset = this.bufferPreset;
    
    console.log('Buffer preset unlocked - adaptive buffering enabled');
    this.startAdaptiveBuffering();
  }

  /**
   * Set master volume
   *
   * @param volume - Volume level (0.0 to 1.0)
   */
  setVolume(volume: number): void {
    if (this.gainNode) {
      this.gainNode.gain.value = Math.max(0, Math.min(1, volume));
    }
  }

  /**
   * Get current volume
   */
  getVolume(): number {
    return this.gainNode?.gain.value ?? 1.0;
  }

  /**
   * Handle incoming voice data from WebSocket
   * Adds packets to jitter buffer for playback
   * Records jitter samples for adaptive buffering
   */
  private handleVoiceData(data: {
    rawPCM: Uint8Array;
    metadata: {
      sessionId: string;
      participantId: string;
      audioFormat: string;
      sampleRate: number;
      channels: number;
      bitDepth: number;
      frameSize: number;
      durationMs: number | null;
    };
  }): void {
    try {
      const receivedAt = performance.now();
      this.stats.packetsReceived++;

      // Convert Uint8Array to Int16Array (PCM data)
      const pcmData = new Int16Array(
        data.rawPCM.buffer,
        data.rawPCM.byteOffset,
        data.rawPCM.byteLength / 2
      );

      // Create buffered packet
      const packet: BufferedPacket = {
        pcmData,
        timestamp: receivedAt,
        sequence: this.stats.packetsReceived,
        participantId: data.metadata.participantId,
        receivedAt
      };

      // Add to buffer (maintain order)
      this.addToBuffer(packet);

      // Calculate jitter for adaptive buffering (PRD 3)
      // Jitter = variation in inter-arrival times
      if (this.lastPacketArrivalTime > 0) {
        const interArrivalTime = receivedAt - this.lastPacketArrivalTime;
        // Expected inter-arrival based on frame duration (~85ms for 4096 samples at 48kHz)
        // Fix: Use nullish coalescing to handle durationMs === 0 correctly
        const expectedInterval = (data.metadata.durationMs !== undefined && data.metadata.durationMs !== null) 
          ? data.metadata.durationMs 
          : 85;
        const jitterSample = Math.abs(interArrivalTime - expectedInterval);
        
        // Add to rolling window for adaptive buffering
        this.jitterSamples.push(jitterSample);
        if (this.jitterSamples.length > VoicePlaybackService.JITTER_WINDOW_SIZE) {
          this.jitterSamples.shift();
        }
        
        // Update stats jitter (cumulative average)
        this.jitterSum += jitterSample;
        this.stats.jitterMs = this.jitterSum / this.stats.packetsReceived;
      }
      this.lastPacketArrivalTime = receivedAt;

      // Legacy latency tracking
      const latency = receivedAt - this.lastPlayedTime;
      this.lastLatency = latency;

      // Start playback if not already playing and buffer is sufficient
      if (!this.isPlaying && this.getBufferDurationMs() >= this.targetBufferMs) {
        this.startPlayback();
      }

    } catch (error) {
      this.onErrorCallback?.(error as Error, 'handleVoiceData');
      errorManager.reportError(
        'Error handling voice data.',
        ErrorSeverity.ERROR,
        'VoicePlaybackService.handleVoiceData',
        error
      );
    }
  }

  /**
   * Add packet to jitter buffer, maintaining order
   */
  private addToBuffer(packet: BufferedPacket): void {
    const maxBufferMs = BUFFER_PRESETS[this.bufferPreset].maxBufferMs;

    // Check for buffer overrun
    if (this.getBufferDurationMs() >= maxBufferMs) {
      // Drop oldest packet
      this.packetBuffer.shift();
      this.stats.packetsDropped++;
      this.stats.bufferOverruns++;
    }

    // Insert in sequence order
    let inserted = false;
    for (let i = this.packetBuffer.length - 1; i >= 0; i--) {
      if (this.packetBuffer[i].sequence < packet.sequence) {
        this.packetBuffer.splice(i + 1, 0, packet);
        inserted = true;
        break;
      }
    }
    if (!inserted) {
      this.packetBuffer.unshift(packet);
    }

    // Update buffer status
    this.stats.currentBufferMs = this.getBufferDurationMs();
    this.onBufferStatusCallback?.(this.stats.currentBufferMs, this.bufferPreset);
  }

  /**
   * Calculate current buffer duration in milliseconds
   */
  private getBufferDurationMs(): number {
    if (this.packetBuffer.length === 0) return 0;

    let totalSamples = 0;
    for (const packet of this.packetBuffer) {
      totalSamples += packet.pcmData.length;
    }

    return (totalSamples / VOICE_BRIDGE_CONFIG.SAMPLE_RATE) * 1000;
  }

  /**
   * Start playback loop
   */
  private startPlayback(): void {
    if (this.isPlaying || !this.audioContext) return;

    this.isPlaying = true;
    this.scheduledEndTime = this.audioContext.currentTime;
    this.onPlaybackStartCallback?.();

    // Schedule packets at regular intervals
    this.playbackInterval = setInterval(() => {
      this.scheduleNextPackets();
    }, 10); // Check every 10ms

    console.log('Voice playback started');
  }

  /**
   * Stop playback loop
   */
  stopPlayback(): void {
    if (!this.isPlaying) return;

    this.isPlaying = false;

    if (this.playbackInterval) {
      clearInterval(this.playbackInterval);
      this.playbackInterval = null;
    }

    // Clear buffer
    this.packetBuffer = [];
    this.stats.currentBufferMs = 0;

    this.onPlaybackStopCallback?.();
    console.log('Voice playback stopped');
  }

  /**
   * Schedule next packets for playback
   */
  private scheduleNextPackets(): void {
    if (!this.audioContext || !this.gainNode) return;

    // Schedule ahead by buffer duration
    const scheduleAheadTime = this.targetBufferMs / 1000;
    const now = this.audioContext.currentTime;

    // Schedule packets until we've scheduled enough ahead
    while (
      this.packetBuffer.length > 0 &&
      this.scheduledEndTime < now + scheduleAheadTime
    ) {
      const packet = this.packetBuffer.shift();
      if (!packet) break;

      this.playPacket(packet);
      this.stats.packetsPlayed++;
      this.lastPlayedTime = performance.now();

      // Track latency
      const latency = this.lastPlayedTime - packet.receivedAt;
      this.latencySum += latency;
      this.latencyCount++;
      this.stats.averageLatencyMs = this.latencySum / this.latencyCount;
    }

    // Check for buffer underrun
    if (this.packetBuffer.length === 0 && this.isPlaying) {
      const minBufferMs = BUFFER_PRESETS[this.bufferPreset].minBufferMs;
      if (this.getBufferDurationMs() < minBufferMs) {
        this.stats.bufferUnderruns++;
        // Don't stop - wait for more data
      }
    }
  }

  /**
   * Play a single packet through Web Audio API
   */
  private playPacket(packet: BufferedPacket): void {
    if (!this.audioContext || !this.gainNode) return;

    try {
      // Convert Int16 PCM to Float32 for Web Audio
      const float32Data = new Float32Array(packet.pcmData.length);
      for (let i = 0; i < packet.pcmData.length; i++) {
        float32Data[i] = packet.pcmData[i] / 32768.0;
      }

      // Create audio buffer
      const audioBuffer = this.audioContext.createBuffer(
        VOICE_BRIDGE_CONFIG.CHANNELS,
        packet.pcmData.length,
        VOICE_BRIDGE_CONFIG.SAMPLE_RATE
      );
      audioBuffer.copyToChannel(float32Data, 0);

      // Create and configure source
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.gainNode);

      // Schedule playback
      const startTime = Math.max(this.scheduledEndTime, this.audioContext.currentTime);
      source.start(startTime);

      // Update scheduled end time
      this.scheduledEndTime = startTime + audioBuffer.duration;

    } catch (error) {
      this.onErrorCallback?.(error as Error, 'playPacket');
      errorManager.reportError(
        'Error playing audio packet.',
        ErrorSeverity.ERROR,
        'VoicePlaybackService.playPacket',
        error
      );
    }
  }

  /**
   * Get playback statistics
   */
  getStats(): PlaybackStats {
    return { ...this.stats };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.stats = {
      packetsReceived: 0,
      packetsPlayed: 0,
      packetsDropped: 0,
      bufferUnderruns: 0,
      bufferOverruns: 0,
      currentBufferMs: 0,
      averageLatencyMs: 0,
      jitterMs: 0
    };
    this.latencySum = 0;
    this.latencyCount = 0;
    this.jitterSum = 0;
  }

  /**
   * Check if currently playing
   */
  isActive(): boolean {
    return this.isPlaying;
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.stopPlayback();
    
    // Stop adaptive buffering
    this.stopAdaptiveBuffering();

    // Unsubscribe from WebSocket events using same bound reference
    webSocketService.unsubscribe('voice_data', this.boundHandleVoiceData);

    // Clean up gain node
    if (this.gainNode) {
      this.gainNode.disconnect();
      this.gainNode = null;
    }

    // Close audio context
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(e => {
        errorManager.reportError(
          'Error closing AudioContext.',
          ErrorSeverity.WARNING,
          'VoicePlaybackService.dispose',
          e
        );
      });
      this.audioContext = null;
    }

    // Clear callbacks
    this.onPlaybackStartCallback = null;
    this.onPlaybackStopCallback = null;
    this.onBufferStatusCallback = null;
    this.onErrorCallback = null;
    
    // Reset adaptive buffering state
    this.jitterSamples = [];
    this.consecutiveQualityReadings = 0;
    this.lastPacketArrivalTime = 0;

    console.log('VoicePlaybackService disposed');
  }
}

/**
 * Singleton instance of the VoicePlaybackService
 */
export const voicePlaybackService = new VoicePlaybackService();

/**
 * Factory function to create new VoicePlaybackService instances
 *
 * @returns VoicePlaybackService - New instance
 */
export function createVoicePlaybackService(): VoicePlaybackService {
  return new VoicePlaybackService();
}

